import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleFilled,
  FileTextOutlined,
  PlusOutlined,
  SearchOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import axiosInstance from "../../../variables/api/axiosInstance";
import NavBar from "../../../components/NavBar";
import SideBar from "../../../components/SideBar";
import Footer from "../../../components/Footer";
import ActionDropdown from "../../production/components/ActionDropdown";

const { Title, Text } = Typography;

const CAT_COLORS = { MOTO: "blue", AUTO: "orange", CAMION: "purple", BUS: "cyan" };
const getCatColor = (nom) => CAT_COLORS[nom?.toUpperCase()] || "default";

const formatNumber = (v) =>
  v != null ? Number(v).toLocaleString("fr-FR") : "—";

const AvenantsV2 = () => {
  const [avenants, setAvenants] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchText, setSearchText] = useState("");

  // Add/Edit modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedAvenant, setSelectedAvenant] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  // Details modal
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsAvenant, setDetailsAvenant] = useState(null);
  const [detailsData, setDetailsData] = useState([]);
  const [detailsSubmitting, setDetailsSubmitting] = useState(false);
  const [detailsForm] = Form.useForm();

  const fetchAvenants = async () => {
    try {
      const res = await axiosInstance.get("/api/avenant");
      setAvenants(res.data.reverse());
    } catch {
      toast.error("Erreur lors du chargement des avenants");
    }
  };

  useEffect(() => {
    fetchAvenants();
    axiosInstance
      .get("/api/categorie")
      .then((res) => setCategories(res.data))
      .catch(() => toast.error("Erreur lors du chargement des catégories"));
  }, []);

  // ── Add / Edit ────────────────────────────────────────────────────────────
  const openAdd = () => {
    setEditMode(false);
    setSelectedAvenant(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditMode(true);
    setSelectedAvenant(record);
    form.setFieldsValue({
      nom: record.nom,
      code: record.code,
      description: record.description,
      penalite: record.penalite,
    });
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    form.resetFields();
  };

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      if (editMode) {
        await axiosInstance.put("/api/avenant", { ...selectedAvenant, ...values });
        toast.success("Avenant modifié avec succès");
      } else {
        // Duplicate check
        const dup = avenants.find(
          (a) =>
            a.nom.toLowerCase() === values.nom.toLowerCase() ||
            a.code.toLowerCase() === values.code.toLowerCase()
        );
        if (dup) {
          toast.error("Un avenant avec le même nom ou code existe déjà.");
          setSubmitting(false);
          return;
        }
        await axiosInstance.post("/api/avenant", values);
        toast.success("Avenant ajouté avec succès");
      }
      handleModalClose();
      fetchAvenants();
    } catch {
      toast.error(editMode ? "Erreur lors de la modification" : "Erreur lors de l'ajout");
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = (avenantId) => {
    Modal.confirm({
      title: "Supprimer cet avenant ?",
      icon: <ExclamationCircleFilled style={{ color: "#ff4d4f" }} />,
      content: "Cette action est irréversible.",
      okText: "Supprimer",
      okButtonProps: { danger: true },
      cancelText: "Annuler",
      onOk: async () => {
        try {
          await axiosInstance.delete(`/api/avenant/${avenantId}`);
          setAvenants((prev) => prev.filter((a) => a.avenantId !== avenantId));
          toast.success("Avenant supprimé");
        } catch {
          toast.error("Erreur lors de la suppression");
        }
      },
    });
  };

  // ── Details / Accessoires ─────────────────────────────────────────────────
  const openDetails = (record) => {
    setDetailsAvenant(record);

    const data = categories.map((cat) => {
      const existing = record.detailsAvenant?.find(
        (d) => d.categorie.codeCategorie === cat.codeCategorie
      );
      return {
        detailsAvenantId: existing?.detailsAvenantId ?? null,
        categorie: cat,
        accessoires: existing?.accessoires ?? 0,
      };
    });
    setDetailsData(data);

    const formValues = Object.fromEntries(
      data.map((item) => [item.categorie.categorieId, item.accessoires])
    );
    detailsForm.setFieldsValue(formValues);
    setDetailsOpen(true);
  };

  const handleDetailsClose = () => {
    setDetailsOpen(false);
    setDetailsAvenant(null);
    detailsForm.resetFields();
  };

  const handleDetailsSave = async () => {
    setDetailsSubmitting(true);
    try {
      const values = detailsForm.getFieldsValue();
      const updatedDetails = detailsData.map((item) => ({
        ...item,
        accessoires: values[item.categorie.categorieId] ?? 0,
      }));
      const payload = { ...detailsAvenant, detailsAvenant: updatedDetails };
      await axiosInstance.put("/api/avenant/details", payload);
      toast.success("Accessoires mis à jour avec succès");
      handleDetailsClose();
      fetchAvenants();
    } catch {
      toast.error("Erreur lors de la mise à jour");
    } finally {
      setDetailsSubmitting(false);
    }
  };

  // ── Filtered data ─────────────────────────────────────────────────────────
  const filtered = avenants.filter(
    (a) =>
      !searchText ||
      a.nom?.toLowerCase().includes(searchText.toLowerCase()) ||
      a.code?.toLowerCase().includes(searchText.toLowerCase())
  );

  const withAccessoires = avenants.filter(
    (a) => a.detailsAvenant && a.detailsAvenant.length > 0
  ).length;

  const withPenalite = avenants.filter(
    (a) => a.penalite != null && a.penalite !== "" && a.penalite !== 0
  ).length;

  // ── Columns ───────────────────────────────────────────────────────────────
  const columns = [
    {
      title: "Avenant",
      key: "avenant",
      render: (_, r) => (
        <Space>
          <SettingOutlined style={{ color: "#667eea" }} />
          <div>
            <Text strong>{r.nom}</Text>
            <br />
            <Tag style={{ borderRadius: 20, fontSize: 11 }}>{r.code}</Tag>
          </div>
        </Space>
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      ellipsis: true,
      render: (v) => v || <Text type="secondary">—</Text>,
    },
    {
      title: "Pénalité",
      dataIndex: "penalite",
      key: "penalite",
      width: 100,
      align: "center",
      render: (v) =>
        v != null && v !== "" ? (
          <Tag color="orange" style={{ borderRadius: 20 }}>
            {v}
          </Tag>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: "Accessoires",
      key: "accessoires",
      width: 130,
      align: "center",
      render: (_, r) => {
        const count = r.detailsAvenant?.length ?? 0;
        return count > 0 ? (
          <Tag color="green" style={{ borderRadius: 20 }}>
            {count} catégorie{count > 1 ? "s" : ""}
          </Tag>
        ) : (
          <Tag color="default" style={{ borderRadius: 20 }}>
            Non configuré
          </Tag>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      align: "center",
      render: (_, record) => (
        <ActionDropdown
          actions={[
            {
              key: "details",
              label: "Accessoires",
              icon: <FileTextOutlined />,
              onClick: () => openDetails(record),
            },
            {
              key: "edit",
              label: "Modifier",
              icon: <EditOutlined />,
              onClick: () => openEdit(record),
            },
            {
              key: "delete",
              label: "Supprimer",
              icon: <DeleteOutlined />,
              danger: true,
              onClick: () => confirmDelete(record.avenantId),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <div className="layout-wrapper layout-content-navbar">
      <div className="layout-container">
        <SideBar currentMenu={"TARIFICATION"} />
        <div className="layout-page">
          <NavBar />
          <div className="content-wrapper">
            <div className="container-xxl flex-grow-1 container-p-y">

              {/* Header */}
              <div
                style={{
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  borderRadius: 12,
                  padding: "24px 28px",
                  marginBottom: 24,
                  color: "#fff",
                }}
              >
                <Row justify="space-between" align="middle">
                  <Col>
                    <Space align="center">
                      <SettingOutlined style={{ fontSize: 36 }} />
                      <div>
                        <Title level={3} style={{ color: "#fff", margin: 0 }}>
                          Gestion des Avenants
                        </Title>
                        <Text style={{ color: "rgba(255,255,255,0.8)" }}>
                          Paramétrage des types d'avenants et de leurs accessoires
                        </Text>
                      </div>
                    </Space>
                  </Col>
                  <Col>
                    <Button
                      icon={<PlusOutlined />}
                      size="large"
                      onClick={openAdd}
                      style={{
                        background: "#fff",
                        color: "#667eea",
                        border: "none",
                        fontWeight: 600,
                        borderRadius: 8,
                      }}
                    >
                      Nouvel avenant
                    </Button>
                  </Col>
                </Row>
              </div>

              {/* Stats */}
              <Row gutter={16} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={8} md={6}>
                  <Card bordered={false} style={{ borderRadius: 10, textAlign: "center" }}>
                    <Statistic
                      title="Total avenants"
                      value={avenants.length}
                      prefix={<SettingOutlined />}
                      valueStyle={{ color: "#667eea" }}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={8} md={6}>
                  <Card bordered={false} style={{ borderRadius: 10, textAlign: "center" }}>
                    <Statistic
                      title="Accessoires configurés"
                      value={withAccessoires}
                      prefix={<FileTextOutlined />}
                      valueStyle={{ color: "#52c41a" }}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={8} md={6}>
                  <Card bordered={false} style={{ borderRadius: 10, textAlign: "center" }}>
                    <Statistic
                      title="Avec pénalité"
                      value={withPenalite}
                      prefix={<ExclamationCircleFilled />}
                      valueStyle={{ color: "#fa8c16" }}
                    />
                  </Card>
                </Col>
              </Row>

              {/* Table card */}
              <Card bordered={false} style={{ borderRadius: 12 }} styles={{ body: { padding: 0 } }}>
                <div style={{ padding: "16px 24px" }}>
                  <Row justify="end">
                    <Col>
                      <Input
                        prefix={<SearchOutlined />}
                        placeholder="Rechercher un avenant..."
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        style={{ width: 260, borderRadius: 8 }}
                        allowClear
                      />
                    </Col>
                  </Row>
                </div>
                <Divider style={{ margin: "0 0 4px" }} />
                <Table
                  columns={columns}
                  dataSource={filtered}
                  rowKey="avenantId"
                  pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    showTotal: (total) => `${total} avenant(s)`,
                  }}
                />
              </Card>
            </div>
            <Footer />
            <div className="content-backdrop fade" />
          </div>
        </div>
      </div>

      {/* ── Add / Edit Modal ── */}
      <Modal
        open={modalOpen}
        onCancel={handleModalClose}
        title={
          <Space>
            <SettingOutlined style={{ color: "#667eea" }} />
            <span>{editMode ? "Modifier l'avenant" : "Nouvel avenant"}</span>
          </Space>
        }
        footer={null}
        width={560}
        destroyOnClose
      >
        <Divider style={{ margin: "8px 0 20px" }} />
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={16}>
            <Col xs={24} sm={14}>
              <Form.Item
                name="nom"
                label="Nom de l'avenant"
                rules={[{ required: true, message: "Le nom est requis" }]}
              >
                <Input placeholder="Ex: Résiliation, Renouvellement..." />
              </Form.Item>
            </Col>
            <Col xs={24} sm={10}>
              <Form.Item
                name="code"
                label="Code"
                rules={[{ required: true, message: "Le code est requis" }]}
              >
                <Input placeholder="Ex: RES, REN..." />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: "La description est requise" }]}
          >
            <Input.TextArea rows={3} placeholder="Description de l'avenant..." />
          </Form.Item>

          <Form.Item name="penalite" label="Pénalité">
            <Input placeholder="Montant ou description de la pénalité" />
          </Form.Item>

          <Divider />
          <Row justify="end" gutter={8}>
            <Col>
              <Button onClick={handleModalClose}>Annuler</Button>
            </Col>
            <Col>
              <Button
                type="primary"
                htmlType="submit"
                loading={submitting}
                style={{
                  background: editMode
                    ? "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
                    : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  border: "none",
                  borderRadius: 8,
                }}
              >
                {editMode ? "Enregistrer les modifications" : "Créer l'avenant"}
              </Button>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* ── Details / Accessoires Modal ── */}
      <Modal
        open={detailsOpen}
        onCancel={handleDetailsClose}
        title={
          <Space>
            <FileTextOutlined style={{ color: "#11998e" }} />
            <span>
              Accessoires —{" "}
              <Text strong style={{ color: "#11998e" }}>
                {detailsAvenant?.nom}
              </Text>{" "}
              <Tag style={{ borderRadius: 20 }}>{detailsAvenant?.code}</Tag>
            </span>
          </Space>
        }
        footer={
          <Row justify="end" gutter={8}>
            <Col>
              <Button onClick={handleDetailsClose}>Annuler</Button>
            </Col>
            <Col>
              <Button
                type="primary"
                loading={detailsSubmitting}
                onClick={handleDetailsSave}
                style={{
                  background: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
                  border: "none",
                  borderRadius: 8,
                  color: "#fff",
                }}
              >
                Enregistrer
              </Button>
            </Col>
          </Row>
        }
        width={560}
        destroyOnClose
      >
        <Divider style={{ margin: "8px 0 16px" }} />
        <Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
          Définissez les frais accessoires par catégorie pour cet avenant.
        </Text>
        <Form form={detailsForm} layout="vertical">
          {detailsData.map((item) => (
            <Row key={item.categorie.categorieId} align="middle" gutter={12} style={{ marginBottom: 12 }}>
              <Col flex="140px">
                <Tag
                  color={getCatColor(item.categorie.nom)}
                  style={{ borderRadius: 20, padding: "3px 12px", fontSize: 13 }}
                >
                  {item.categorie.nom}
                </Tag>
              </Col>
              <Col flex="auto">
                <Form.Item
                  name={item.categorie.categorieId}
                  style={{ margin: 0 }}
                  rules={[{ required: true, message: "Obligatoire" }]}
                >
                  <InputNumber
                    min={0}
                    style={{ width: "100%" }}
                    formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")}
                    parser={(v) => v.replace(/\s/g, "")}
                    addonAfter="FCFA"
                  />
                </Form.Item>
              </Col>
            </Row>
          ))}
        </Form>
      </Modal>

      <div className="layout-overlay layout-menu-toggle" />
      <div className="drag-target" />
    </div>
  );
};

export default AvenantsV2;
